import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./style.scss";
import { ChatForm } from "../Components/ChatForm";
import { Chat } from "./Chat";
import {
  Chat as ChatType,
  DownloadType,
  Reference as ReferenceType
} from "models/AiResearch";
import { SideReference } from "components/AiResearch/SideReference";
import CaretDownImage from "images/icon/caret-down.svg";

const OFFSET_WIDTH = 48;
const MIN_CHAT_WIDTH = 532;
const SCROLL_OFFSET = 48;

interface Props {
  chat: ChatType;
  reference?: ReferenceType | null;
  handleSubmit: (text: string) => void;
  handleCopy: ({ chatIndex }: { chatIndex: number }) => void;
  handleDownload: ({
    type,
    questionId
  }: {
    type: DownloadType;
    questionId: string;
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
  isReferenceLoading: boolean;
  isOpen: boolean;
  onShowReference: ({
    questionId,
    citationNo
  }: {
    questionId: string;
    citationNo: number;
  }) => void;
  clearReference: () => void;
}

export const History: React.FC<Props> = ({
  chat,
  reference,
  handleSubmit,
  handleCopy,
  handleDownload,
  onCancel,
  isLoading,
  isReferenceLoading,
  isOpen,
  onShowReference,
  clearReference
}) => {
  const [referenceWidth, setReferenceWidth] = useState<number>(436);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [prefill, setPrefill] = useState<string>("");

  const handleReferenceClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest?.("[data-reference-button]")) return false;
  }, []);

  const handleShowReference = ({
    questionId,
    referenceId
  }: {
    questionId: string;
    referenceId: number;
  }) => {
    onShowReference({ questionId, citationNo: referenceId });
  };

  const chatWidth = containerRef.current
    ? Math.max(
        MIN_CHAT_WIDTH,
        containerRef.current.clientWidth - referenceWidth - OFFSET_WIDTH
      )
    : undefined;

  useEffect(() => {
    const element =
      (document.scrollingElement as HTMLElement | null) ||
      document.documentElement;
    if (!element) return;

    const update = () => {
      const scrollTop = element.scrollTop;
      const clientHeight = element.clientHeight;
      const scrollHeight = element.scrollHeight;
      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom =
        scrollTop + clientHeight >= scrollHeight - SCROLL_OFFSET;
      setShowScrollDown(isScrollable && !isAtBottom);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(element);

    return () => {
      window.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [chat, referenceWidth]);

  // 画面スクロール処理
  const handleScrollDown = useCallback(() => {
    const element =
      (document.scrollingElement as HTMLElement | null) ||
      document.documentElement;
    if (!element) return;

    element.scrollTo({ top: element.scrollHeight, behavior: "smooth" });
  }, []);

  const handleClearReference = () => {
    clearReference();
    setReferenceWidth(436);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <div
        className={styles.chatContainer}
        ref={chatContainerRef}
        style={{ width: reference ? chatWidth : "100%" }}
      >
        <Chat
          chat={chat}
          selectedReference={reference}
          handleSelectRecommendation={text => setPrefill(text)}
          handleShowReference={handleShowReference}
          handleCopy={handleCopy}
          handleDownload={handleDownload}
        />

        <div className={styles.chatForm}>
          <div className={styles.chatFormInner}>
            {showScrollDown && (
              <button
                className={styles.scrollButton}
                onClick={handleScrollDown}
              >
                <img src={CaretDownImage} alt="下へスクロール" />
              </button>
            )}
            <ChatForm
              type="history"
              prefillText={prefill}
              onSubmit={text => {
                handleSubmit(text);
              }}
              onCancel={onCancel}
              isLoading={isLoading || isOpen}
            />
            <div className={styles.disclaimer}>
              生成AIの回答は必ずしも正しいとは限りません。正確な情報はソースを確認してください。
            </div>
          </div>
        </div>
      </div>

      {isReferenceLoading || reference ? (
        <SideReference
          isLoading={isReferenceLoading}
          reference={reference}
          onClose={handleClearReference}
          onChangeWidth={width => setReferenceWidth(width)}
          handleClickOutside={handleReferenceClickOutside}
        />
      ) : null}
    </div>
  );
};
